import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { AppDispatch, RootState } from 'redux/store';
import TransactionDetails from 'components/Common/Transaction/TransactionDetails';
import { fetchRequestMoneyDetail, requestUpdateStatus } from 'redux/slices/sendAndRequest/requestMoneyDetailSlice';
import Routes from 'constants/routes';
import { getRequestTransactionDetails, isInvoiceReceiver } from 'utils/helpers';
import { notify } from 'components/Common/Toast';
import CustomButton from 'components/Common/CustomButton';
import { Checkbox, FormControlLabel, Stack } from '@mui/material';
import ConfirmRequestMoneyForm from 'pages/SendAndRequest/ConfirmRequestMoneyForm';

const RequestMoneyTransactionDetail = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { transactionId } = useParams();
    const [buttonLoading, setButtonLoading] = useState<boolean>(false);
    const [checked, setChecked] = useState<boolean>();
    const { requestMoneyDetail, loading } = useSelector((state: RootState) => state?.sendAndRequest?.requestMoneyDetail);
    const { myRole = '', requestMoneyId, status } = requestMoneyDetail || {};
    const [showForm, setShowForm] = useState(false);
    const backButtonNavigation = () => {
        navigate(Routes.sendAndRequest.main);
    };

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        transactionId && dispatch(fetchRequestMoneyDetail(transactionId));
    }, [transactionId, dispatch]);

    const isReceiver = isInvoiceReceiver(myRole);

    const updateStatus = async (status: string, moneyContainerId?: number) => {
        if (!requestMoneyId) return;
        const isBusiness: any = !checked && requestMoneyDetail?.user;
        let body: any = { requestId: requestMoneyId, newStatus: status, isBusiness };
        if (moneyContainerId) {
            body.moneyContainerId = moneyContainerId;
        }
        try {
            setButtonLoading(true);
            const res: any = await dispatch(requestUpdateStatus({ ...body }));
            const { code } = res?.payload || {};

            if (code === 200) {
                switch (status) {
                    case 'PAID':
                        notify('Payment sent successfully.', 'success');
                        setShowForm(false);
                        break;
                    case 'DECLINED':
                        notify('Invoice declined successfully.', 'success');
                        break;
                    case 'CANCELED':
                        notify('Request canceled successfully.', 'success');
                        break;
                }

                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                requestMoneyId && dispatch(fetchRequestMoneyDetail(String(requestMoneyId)));
            }
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setButtonLoading(false);
        }
    };

    return (
        <>
            {showForm ? (
                <ConfirmRequestMoneyForm
                    details={getRequestTransactionDetails(requestMoneyDetail)}
                    handleUpdateStatus={updateStatus}
                    backButtonNavigation={() => setShowForm(false)}
                />
            ) : (
                <TransactionDetails
                    sectionTitle={isReceiver ? 'Request Money' : null}
                    cancelButtonProps={{
                        loading: buttonLoading,
                        onClick: () => updateStatus('DECLINED'),
                        disabled: status !== 'PENDING',
                        title: 'Decline',
                    }}
                    submitButtonProps={{
                        loading: buttonLoading,
                        onClick: () => setShowForm(true),
                        disabled: status !== 'PENDING',
                        title: status === 'PENDING' ? 'Complete' : 'PAID',
                    }}
                    loading={loading}
                    title={isInvoiceReceiver(myRole) ? 'From' : 'To'}
                    details={getRequestTransactionDetails(requestMoneyDetail)}
                    backButtonNavigation={backButtonNavigation}
                    showButtons={isReceiver}
                >
                    {isReceiver && requestMoneyDetail?.user && status === 'PENDING' && (
                        <Stack ml={14} mb={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={(e) => setChecked(e.target.checked)}
                                        checked={checked}
                                        sx={{
                                            color: '#d3d3d3',
                                            '&.Mui-checked': {
                                                color: '#142456',
                                            },
                                        }}
                                    />
                                }
                                label="Business transaction?"
                            />
                        </Stack>
                    )}
                    {!isReceiver && (
                        <Stack alignItems={'center'}>
                            <CustomButton
                                title="Cancel"
                                onClick={() => updateStatus('CANCELED')}
                                loading={buttonLoading}
                                disabled={status !== 'PENDING'}
                                sx={{ width: '40%', backgroundColor: '#B91C1C' }}
                            />
                        </Stack>
                    )}
                </TransactionDetails>
            )}
        </>
    );
};
export default RequestMoneyTransactionDetail;
